import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';

import Portal from '@bibliocommons/base-portal';
import { getHistoryState } from '@bibliocommons/utils-browser';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { childrenShape } from '@bibliocommons/bc-prop-types';
import { IconButton } from '@bibliocommons/base-buttons';
import { Close } from '@bibliocommons/base-icons';
import Context from '@bibliocommons/context';

import './Modal.scss';

const ESCAPE_KEY_NAME = 'Escape';
const ESCAPE_KEY_CODE = 27;

function close() {
  window.history.back();
}

function closeOnEscape(e) {
  if (e.key === ESCAPE_KEY_NAME || e.which === ESCAPE_KEY_CODE) {
    close();
  }
}

function Modal({ open = false, className, bodyClassName, children, dataKey, size = 'default', handleClose }) {
  const context = useContext(Context);

  const modalBodyRef = useRef(null);
  const modalIdRef = useRef(null);
  if (modalIdRef.current === null) {
    modalIdRef.current = uniqueId('modalId');
  }

  // Keep the latest prop values available to stable event handlers without
  // changing their identity (so addEventListener/removeEventListener pair up).
  const openRef = useRef(open);
  const handleCloseRef = useRef(handleClose);
  openRef.current = open;
  handleCloseRef.current = handleClose;

  const adjustModalHeight = useCallback(() => {
    // We must adjust the modal body's height because some
    // mobile browsers cover the bottom with the browser chrome.

    if (modalBodyRef.current && window) {
      const screenXsMaxWidth = 575; // $screen-xs-max CSS variable value

      if (window.innerWidth <= screenXsMaxWidth) {
        const maxHeightPx = `${window.innerHeight}px`;

        modalBodyRef.current.style.maxHeight = maxHeightPx;
        modalBodyRef.current.style.minHeight = maxHeightPx;
      } else {
        modalBodyRef.current.style.maxHeight = null;
        modalBodyRef.current.style.minHeight = null;
      }
    }
  }, []);

  const debouncedAdjustModalHeight = useMemo(() => debounce(adjustModalHeight, 250), [adjustModalHeight]);

  const closeOnPopState = useCallback(() => {
    const historyState = getHistoryState();

    if (openRef.current && historyState.modalId !== modalIdRef.current) {
      handleCloseRef.current();
    }
  }, []);

  const closeOnDocumentClick = useCallback(e => {
    // If clicking outside the modal's content, call the close handler
    const contentIsTarget = modalBodyRef.current && modalBodyRef.current.contains(e.target);
    if (document.body.contains(e.target) && !contentIsTarget) {
      close();
    }
  }, []);

  const handleToggle = useCallback(
    isOpen => {
      // Add the event listener on open, and remove on close
      const handleEventListener = isOpen ? 'addEventListener' : 'removeEventListener';
      document[handleEventListener]('click', closeOnDocumentClick, true);
      document[handleEventListener]('keydown', closeOnEscape, true);
      window[handleEventListener]('popstate', closeOnPopState);
      window[handleEventListener]('resize', debouncedAdjustModalHeight);

      const handleClass = isOpen ? 'add' : 'remove';
      document.documentElement.classList[handleClass]('modal-is-open');

      const historyState = getHistoryState();

      if (isOpen) {
        window.history.pushState({ ...historyState, modalId: modalIdRef.current }, '');
        debouncedAdjustModalHeight();
      } else if (historyState.modalId === modalIdRef.current) {
        close();
      }
    },
    [closeOnDocumentClick, closeOnPopState, debouncedAdjustModalHeight]
  );

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      if (open) {
        handleToggle(open);
      }
    } else {
      handleToggle(open);
    }
  }, [open, handleToggle]);

  useEffect(
    () => () => {
      handleToggle(false);
    },
    [handleToggle]
  );

  const classNames = cn('cp-modal', className);
  const bodyClassNames = cn('modal__body', bodyClassName, {
    [`modal__body--${size}`]: !!size
  });

  return (
    <Portal focusTrapActive={open}>
      <CSSTransition in={open} classNames="modal-fade" timeout={{ enter: 150, exit: 75 }} mountOnEnter unmountOnExit>
        <div className={classNames} role="dialog" aria-hidden={!open} data-key={dataKey}>
          <div className={bodyClassNames} ref={modalBodyRef}>
            <div>{children}</div>
            {!context.mobileApp && (
              <IconButton dataKey="modal-close" className="modal__body__close" handleClick={close}>
                <Close />
                <ScreenReaderMessage>
                  <FormattedMessage id="close_modal" />
                </ScreenReaderMessage>
              </IconButton>
            )}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
}

Modal.propTypes = {
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  children: childrenShape.isRequired,
  dataKey: PropTypes.string,
  open: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small', 'large'])
};

export default Modal;
